import { useSyncExternalStore } from "react";
import { Alert, Snackbar } from "@mui/material";

// A tiny global snackbar store with a zustand-style selector hook, so callers can
// write `const notify = useSnackbarStore((s) => s.notify)`. Render <SnackbarHost/>
// once near the app root for the toasts to appear.

export type SnackbarSeverity = "success" | "error" | "warning" | "info";

type Toast = { id: number; message: string; severity: SnackbarSeverity };

let toasts: Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const emit = () => listeners.forEach((l) => l());

export const snackbar = {
  notify(message: string, severity: SnackbarSeverity = "success") {
    toasts = [...toasts, { id: nextId++, message, severity }];
    emit();
  },
  dismiss(id: number) {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  },
};

const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};
const getSnapshot = () => toasts;

/** zustand-style selector hook over the module store. */
export function useSnackbarStore<T>(selector: (s: typeof snackbar) => T): T {
  return selector(snackbar);
}

/** Mount once near the app root. Shows one toast at a time. */
export function SnackbarHost() {
  const current = useSyncExternalStore(subscribe, getSnapshot, getSnapshot)[0];
  return (
    <Snackbar
      open={Boolean(current)}
      autoHideDuration={3000}
      onClose={() => current && snackbar.dismiss(current.id)}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    >
      {current ? (
        <Alert
          severity={current.severity}
          variant="filled"
          onClose={() => snackbar.dismiss(current.id)}
        >
          {current.message}
        </Alert>
      ) : undefined}
    </Snackbar>
  );
}
